import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Add, Delete, Save, Edit } from '@mui/icons-material';
import { aboutApi } from '../services/api';
import { FileUpload } from './components/FileUpload';

interface BusinessAreaItem {
  id: string;
  title: string;
  description: string;
  iconUrl: string;
}

interface BusinessAreaData {
  mainTitle: string;
  mainDescription: string;
  items: BusinessAreaItem[];
}

export const BusinessAreaEditor: React.FC = () => {
  const [businessAreaData, setBusinessAreaData] = useState<BusinessAreaData>({
    mainTitle: 'ABOUT US',
    mainDescription: '클라이언트가 원하는 요구사항을 정확하고 신속하게 제공합니다.',
    items: [
      {
        id: '1',
        title: 'BRAND STRATEGY',
        description: '브랜드 특성에 맞는 다양한 형태의\n캠페인 전략 수립',
        iconUrl: '/img/home/icon_circle1.png',
      },
      {
        id: '2',
        title: 'CONTENTS MARKETING',
        description: 'SNS기획 - 운영 - 제작 등\nSNS 마케팅',
        iconUrl: '/img/home/icon_circle2.png',
      },
      {
        id: '3',
        title: 'PERFORMANCE MARKETING',
        description: 'UX,UI /웹사이트/ \n앱&솔루션 개발 / 운영지원',
        iconUrl: '/img/home/icon_circle3.png',
      },
    ],
  });
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showIconDialog, setShowIconDialog] = useState(false);
  const [newIconUrl, setNewIconUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBusinessAreaData();
  }, []);

  const loadBusinessAreaData = async () => {
    setLoading(true);
    try {
      const data = await aboutApi.get();
      if (data && data.businessArea) {
        setBusinessAreaData(prev => ({ ...prev, ...data.businessArea }));
      }
    } catch (error) {
      console.error('Business Area 데이터 로드 실패:', error);
      setErrorMessage('Business Area 데이터를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aboutApi.update({ businessArea: businessAreaData });
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleMainTitleChange = (title: string) => {
    setBusinessAreaData(prev => ({ ...prev, mainTitle: title }));
  };

  const handleMainDescriptionChange = (description: string) => {
    setBusinessAreaData(prev => ({ ...prev, mainDescription: description }));
  };

  const handleItemTitleChange = (itemId: string, title: string) => {
    setBusinessAreaData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, title } : item
      ),
    }));
  };

  const handleItemDescriptionChange = (itemId: string, description: string) => {
    setBusinessAreaData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, description } : item
      ),
    }));
  };

  const handleIconChange = (itemId: string, iconUrl: string) => {
    setBusinessAreaData(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, iconUrl } : item
      ),
    }));
  };

  const handleIconUpload = (itemId: string) => (fileUrl: string) => {
    handleIconChange(itemId, fileUrl);
  };

  const handleAddIcon = () => {
    if (editingItemId && newIconUrl) {
      handleIconChange(editingItemId, newIconUrl);
      setNewIconUrl('');
      setShowIconDialog(false);
    }
  };

  const handleAddItem = () => {
    const newItem: BusinessAreaItem = {
      id: Date.now().toString(),
      title: '새 비즈니스 영역',
      description: '새로운 비즈니스 영역에 대한 설명',
      iconUrl: '/img/home/icon_circle1.png',
    };
    setBusinessAreaData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleRemoveItem = (itemId: string) => {
    setBusinessAreaData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId),
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Business Area 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Business Area 관리
        </Typography>
        
        <Grid container spacing={3}>
          {/* 메인 정보 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              메인 정보
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="메인 제목"
                  value={businessAreaData.mainTitle}
                  onChange={(e) => handleMainTitleChange(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="메인 설명"
                  value={businessAreaData.mainDescription}
                  onChange={(e) => handleMainDescriptionChange(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 비즈니스 영역 아이템들 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                비즈니스 영역 ({businessAreaData.items.length}개)
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddItem}
              >
                영역 추가
              </Button>
            </Box>

            <Grid container spacing={3}>
              {businessAreaData.items.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          영역 {item.id}
                        </Typography>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveItem(item.id)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      
                      {/* 텍스트 필드들 */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="제목"
                            value={item.title}
                            onChange={(e) => handleItemTitleChange(item.id, e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="설명"
                            value={item.description}
                            onChange={(e) => handleItemDescriptionChange(item.id, e.target.value)}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={3}
                          />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* 아이콘 업로드 섹션 */}
                      <Typography variant="subtitle2" gutterBottom>
                        아이콘 업로드
                      </Typography>
                      
                      {item.iconUrl && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <img
                            src={item.iconUrl}
                            alt="아이콘"
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: 'contain',
                              borderRadius: 8,
                            }}
                          />
                        </Box>
                      )}
                      
                      <FileUpload
                        onUploadComplete={handleIconUpload(item.id)}
                        accept="image/*"
                        title="아이콘 업로드"
                        description="비즈니스 영역 아이콘을 업로드하세요"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* 저장 버튼 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                size="large"
              >
                {saving ? '저장 중...' : '저장'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 아이콘 편집 다이얼로그 */}
      <Dialog open={showIconDialog} onClose={() => setShowIconDialog(false)}>
        <DialogTitle>아이콘 경로 편집</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="아이콘 경로"
            value={newIconUrl}
            onChange={(e) => setNewIconUrl(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIconDialog(false)}>취소</Button>
          <Button onClick={handleAddIcon} variant="contained">확인</Button>
        </DialogActions>
      </Dialog>

      {/* 성공 알림 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Business Area 정보가 성공적으로 저장되었습니다.
        </Alert>
      </Snackbar>

      {/* 에러 알림 */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 